// @flow
//
// Note: this component is currently used for the school name in the grad
// program form and for cms content article names. It is generalized so other
// Algolia based sources can be used by adding new AutocompleteType options.
import * as React from 'react';
import styled from 'styled-components';
import * as SS from 'styled-system';
import Autosuggest from 'react-autosuggest';
import { colors } from '../theme';

const { useState, useCallback } = React;

const AutosuggestWrapper = styled.div`
  position: relative;

  .react-autosuggest {

    &__input {
      margin: 0;
      padding: 11px 1em;
      font: inherit;
      font-size: 18px;
      color: ${colors.charcoalGrey};
      border-radius: 4px;
      border: solid 1px;
      border-color: ${p => (p.hasError ? colors.scarlet : colors.silver)};
      height: 45px;
      width: 100%;
      background-color: ${colors.white};

      ::placeholder {
        color: ${colors.middleGrey};
      }

      &:focus {
        background-color: ${colors.offWhite};
      }

      &:disabled {
        background-color: ${colors.lightGrey};
      }

      ${SS.width} ${SS.height} ${SS.color} ${SS.space} ${SS.fontSize}
      ${SS.borders} ${SS.borderRadius} ${SS.borderColor};
    }

    &__suggestions-container {
      display: none;

      &--open {
        display: block;
        position: absolute;
        width: 100%;
        border: 1px solid;
        border-color: ${colors.silver};
        background-color: #fff;
        font-size: 18px;
        line-height: 19px;
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
        z-index: 2;
      }
    }

    &__suggestions-list {
      margin: 0;
      padding: 0;
      list-style-type: none;
    }

    &__suggestion {
      cursor: pointer;
      padding: 10px 1em;

      &--highlighted {
        background-color: ${colors.offWhite};
      }
    }
  }
`;

const renderSuggestion = suggestion => <span>{suggestion}</span>;

const getSuggestionValue = suggestion => suggestion;

type Props = {
  onChange: Object => void,
  onSuggestionSelected?: Function,
  shouldRenderSuggestions?: Function,
  options: Array<string>,
  hasError: boolean
};

const AutocompleteTextInput = ({
  onChange,
  onSuggestionSelected,
  shouldRenderSuggestions,
  options,
  hasError,
  ...otherProps
}: Props) => {
  const [suggestions, setSuggestions] = useState(options);

  const onChangeInternal = (
    event: Object,
    { newValue }: { newValue: string }
  ) => {
    onChange({ target: { value: newValue } });
  };

  const onSuggestionsFetchRequested = useCallback(
    ({ value }: { value: ?string }) => {
      if (value && value.length) {
        const searchValue = value.toLowerCase().replace(/[^A-Za-z0-9]/g, '');
        const newSuggestions = options.filter(option =>
          option.replace(/[^A-Za-z0-9]/g, '').includes(searchValue)
        );
        setSuggestions(newSuggestions);
      } else {
        setSuggestions(options);
      }
    },
    [options]
  );

  const onSuggestionsClearRequested = useCallback(
    () => setSuggestions(options),
    [options]
  );

  const inputProps = {
    ...otherProps,
    placeholder: '',
    onChange: onChangeInternal
  };

  return (
    <AutosuggestWrapper>
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        onSuggestionSelected={onSuggestionSelected}
        shouldRenderSuggestions={shouldRenderSuggestions}
        renderSuggestion={renderSuggestion}
        inputProps={inputProps}
      />
    </AutosuggestWrapper>
  );
};

export default AutocompleteTextInput;
